import React from 'react';
import Icon from 'components/Utils/NewIcon';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import DefaultTimePicker from 'components/Utils/DefaultTimePicker';
import MenuItem from '@material-ui/core/MenuItem';
import List from '@material-ui/core/List';
import Divider from '@material-ui/core/Divider';
import ClinicUtils from 'services/ClinicUtils';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import ProgressMessage from 'components/Utils/ProgressMessage';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import UnreadBadge from 'components/Common/UnreadBadge';
import StaffTimePicker from 'components/Utils/StaffTimePicker';
import DefaultPatientTextField from 'components/Utils/DefaultPatientTextField';
import { Switch } from 'antd';
import { Controller } from 'react-hook-form';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import { getTenantIdentity } from 'hooks/useTenantIdentity';

export default class UIUtils {
  static get WHITE_BADGE_STYLE() {
    return {
      height: '14px',
      minWidth: '14px',
      width: 'auto',
      borderRadius: '25px',
      color: '#2693d1',
      fontSize: '11px',
      border: '1px solid var(--color-border-default)',
      position: 'absolute',
      top: '-5px',
      right: '-5px',
      background: 'white',
    };
  }

  static get WHITE_WIDE_BADGE_STYLE() {
    let style = this.WHITE_BADGE_STYLE;
    style.padding = '0 3px';
    style.right = '-10px';
    return style;
  }

  static get SELECT_FIELD_STYLE() {
    return {
      width: '100%',
    };
  }

  static get SELECT_FIELD_MENU_ITEM_STYLE() {
    return { color: '#2693d1' };
  }

  static get TEXT_FIELD_COLOR() {
    return '#b4b4b4';
  }

  static get TEXT_VALUE_COLOR() {
    return '#838395';
  }

  static get SEARCH_TYPE_TIMEOUT() {
    return 350;
  }

  static getRelativeCoordinates(event, reference) {
    var x, y;
    event = event || window.event;
    var el = event.target || event.srcElement;

    if (!window.opera && typeof event.offsetX !== 'undefined') {
      // Use offset coordinates and find common offsetParent
      var pos = { x: event.offsetX, y: event.offsetY };

      // Send the coordiates upwards through the offsetParent chain.
      var e = el;
      while (e) {
        e.mouseX = pos.x;
        e.mouseY = pos.y;
        pos.x += e.offsetLeft;
        pos.y += e.offsetTop;
        e = e.offsetParent;
      }

      // Look for the coordinates starting from the reference element.
      e = reference;
      var offset = { x: 0, y: 0 };
      while (e) {
        if (typeof e.mouseX !== 'undefined') {
          x = e.mouseX - offset.x;
          y = e.mouseY - offset.y;
          break;
        }
        offset.x += e.offsetLeft;
        offset.y += e.offsetTop;
        e = e.offsetParent;
      }

      // Reset stored coordinates
      e = el;
      while (e) {
        e.mouseX = undefined;
        e.mouseY = undefined;
        e = e.offsetParent;
      }
    } else {
      // Use absolute coordinates
      pos = UIUtils.getAbsolutePosition(reference);
      x = event.pageX - pos.x;
      y = event.pageY - pos.y;
    }
    // Subtract distance to middle
    y += event.target.parentNode.parentNode.scrollTop;
    return { x: x, y: y };
  }

  static getAbsolutePosition(element) {
    var r = { x: element.offsetLeft, y: element.offsetTop };
    if (element.offsetParent) {
      var tmp = UIUtils.getAbsolutePosition(element.offsetParent);
      r.x += tmp.x;
      r.y += tmp.y;
    }
    return r;
  }

  //New Field stuff

  static getPatientTextField(field) {
    return <DefaultPatientTextField field={field} />;
  }

  static getTextField(field) {
    return <DefaultTextField field={field} />;
  }

  static getTextAreaAutosize(field) {
    return <TextareaAutosize cacheMeasurements {...field} {...field.input} />;
  }

  static getToggle(field) {
    return (
      <DefaultSwitch
        name={field.name}
        id={field.id}
        onChange={(event, isInputChecked) => field.input.onChange(isInputChecked)}
        label={field.label}
        loading={field.loading}
        disabled={field.disabled}
        padded={field.padded}
        checked={field.input.value || false}
        style={field.style}
        reversed={field.reversed}
      />
    );
  }

  static getAutoComplete(field, onInputUpdated) {
    if (typeof onInputUpdated !== 'function') onInputUpdated = undefined;

    return (
      <DefaultAutoComplete
        openOnFocus={true}
        onUpdateInput={(val) => {
          field.input.onChange(val);
          if (onInputUpdated) onInputUpdated(val);
        }}
        field={field}
      />
    );
  }

  static getDateField(field) {
    return <DefaultDatePicker field={field} />;
  }

  static getTimeField(field) {
    return <DefaultTimePicker field={field} />;
  }

  static getStaffTimeField(field) {
    return <StaffTimePicker field={field} />;
  }

  static getSelectField(field) {
    let menuItems =
      field.inputs ||
      field.values.map((value, index) => (
        <MenuItem id={'menu-item-' + value} key={index + value} value={value}>
          {value}
        </MenuItem>
      ));
    return (
      <DefaultSelectField
        style={{
          display: 'inline-block',
          width: '120px',
          marginRight: '20px',
        }}
        label={field.label}
        {...field}
        {...field.input}
        onChange={(event) => {
          if (field.onChange) field.onChange(event);
          return field.input.onChange(event);
        }}
        inputElements={menuItems}
      />
    );
  }

  static exifOrientationToCSS(orientation) {
    switch (orientation) {
      case 2:
        return { transform: 'rotateY(180deg)' };
      case 3:
        return { transform: 'rotate(180deg)' };
      case 4:
        return { transform: 'rotate(180deg) rotateY(180deg)' };
      case 5:
        return { transform: 'rotate(270deg) rotateY(180deg)' };
      case 6:
        return { transform: 'rotate(90deg)' };
      case 7:
        return { transform: 'rotate(90deg) rotateY(180deg)' };
      case 8:
        return { transform: 'rotate(270deg' };
      default:
        return {};
    }
  }
}

export const DetailsPanelTopBar = ({ name, onClose }) => {
  return (
    <div className="top-bar">
      <span className="small-title new-immunization-title">{name}</span>
      {onClose !== undefined && (
        <div className="close">
          <Icon src="xmark" onClick={onClose} />
        </div>
      )}
    </div>
  );
};

export const DefaultDivider = ({ style, className }) => (
  <Divider variant="middle" style={style} className={className} />
);
export const DefaultList = (props) => <List {...props} />;

/**
 * @param {any} Props
 */
export const DefaultSwitch = ({
  control,
  name: propName,
  label,
  loading,
  disabled,
  padded,
  reversed,
  ...props
}) => {
  // RHF mode: when control + name are provided, wrap in Controller
  if (control && propName) {
    return (
      <Controller
        name={propName}
        control={control}
        render={({ field }) => (
          <DefaultSwitch
            name={field.name}
            id={field.name}
            onChange={(_event, checked) => field.onChange(checked)}
            label={label}
            loading={loading}
            disabled={disabled}
            padded={padded}
            checked={field.value || false}
            style={props.style}
            reversed={reversed}
          />
        )}
      />
    );
  }

  let finalClassName = 'default-switch';
  if (padded) finalClassName += ' padded-switch';
  if (reversed) finalClassName += ' reversed';

  return (
    <div className={finalClassName} style={props.style}>
      <span className="switch-label">{label}</span>
      <Switch
        className={'switch-root-' + (props.id || propName)}
        id={props.id || propName}
        disabled={loading || disabled}
        size="default"
        {...props}
        onChange={(checked, e) => {
          // Antd Switch fires (checked, MouseEvent) and the MouseEvent's target is the
          // underlying <button>, which has no `name` attribute. Consumers like
          // PhysicianInfo read `event.target.name` to know which field changed, so we
          // synthesize an input-like event here. NOTE: this is NOT a full SyntheticEvent —
          // preventDefault/stopPropagation/currentTarget are absent. Reach for nativeEvent
          // if you need the real Antd event.
          const syntheticEvent = {
            target: { name: propName, value: checked },
            nativeEvent: e,
          };
          props.onChange(syntheticEvent, checked);
        }}
      />
    </div>
  );
};

export const OfficeHoursString = ({ hours }) => {
  let clinicOpenTimeWeekday = ClinicUtils.getOpeningHours(hours, 'clinic', 0);
  let clinicCloseTimeWeekday = ClinicUtils.getClosingHours(hours, 'clinic', 0);
  let clinicCloseTimeWeekend = ClinicUtils.getClosingHours(hours, 'clinic', 5);

  return (
    <span>
      Office hours:
      <br />
      Mo-Fr: {clinicOpenTimeWeekday} - {clinicCloseTimeWeekday}
      <br />
      Weekends: {clinicCloseTimeWeekend}
      <br />
    </span>
  );
};

/**@param {any} props*/
export const DefaultCheckbox = ({
  name,
  value,
  onChange,
  text,
  style,
  className,
  labelClassName,
  wrapperClassName = '',
  disabled,
  ...props
}) => (
  <div className={`default-checkbox-wrapper ${wrapperClassName}`} style={style}>
    <input
      type="checkbox"
      className={className}
      id={'field-' + name}
      name={name}
      checked={value}
      onChange={onChange}
      disabled={disabled}
      onKeyUp={(ev) => {
        if (ev.key === 'Enter') {
          onChange();
        }
      }}
      {...props}
    />
    {text !== undefined && (
      <label htmlFor={name} className={labelClassName}>
        {' '}
        {text}
      </label>
    )}
  </div>
);

export const ConnectionStatus = ({ status }) => (
  <DefaultTooltip
    placement="top"
    title={`There is a problem reaching the ${
      getTenantIdentity().name
    } server. Please double-check your internet connectivity.`}
  >
    <div className={`connection-status rounded status-${status}`}>
      <span>Connection status:</span>
      <span className="status-label">{status}</span>
    </div>
  </DefaultTooltip>
);

const mediaCardStyles = makeStyles({
  root: {
    maxWidth: 200,
    margin: 10,
  },
  media: {
    height: 70,
  },
});

export const ResponsiveMediaCard = ({
  title,
  text,
  onClick,
  img,
  badgeCount,
  loading = false,
  allowed = true,
}) => {
  return !allowed ? null : (
    <div className="responsive-media-card" onClick={onClick}>
      <div style={{ position: 'absolute', right: '5px', top: '5px' }}>
        <UnreadBadge unreadCount={badgeCount} />
      </div>
      <Icon
        height="50px"
        width="50px"
        fontAwesomeStyle="far"
        className="blue"
        style={{ margin: '20px 0' }}
        src={img}
      />
      <div className="content">
        <ProgressMessage topRight={true} show={loading} />
        <div className="card-title">{title}</div>
        <div className="card-description">{text}</div>
      </div>
    </div>
  );
};

export const MediaCard = ({
  title,
  text,
  onClick,
  img,
  badgeCount,
  loading = false,
  allowed = true,
}) => {
  const classes = mediaCardStyles();

  return (
    allowed && (
      <Card className={`media-card ${classes.root}`} onClick={onClick}>
        <CardActionArea>
          <div style={{ position: 'absolute', right: '5px', top: '5px' }}>
            <UnreadBadge unreadCount={badgeCount} />
          </div>
          <Icon
            height="70px"
            fontAwesomeStyle="far"
            className="blue"
            style={{ margin: '20px 0' }}
            src={img}
          />
          <CardContent>
            <ProgressMessage topRight={true} show={loading} />
            <Typography gutterBottom variant="h5" component="h2" className="blue">
              {title}
            </Typography>
            <Typography variant="body2" color="textSecondary" component="p">
              {text}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    )
  );
};
